import type { i18n as I18nInstance } from 'i18next';
import { useMemo } from 'react';

import { getAuthState } from '@/shared/auth/store';
import { useCurrentUserOrNull } from '@/shared/auth/hooks';
import { useFormatters } from '@/shared/i18n/format';
import { intlTimeZone } from '@/shared/lib/timeZones';

import type { WeekDayCode } from '../api';

import { browserTimeZone, isValidTimeZone, toWallClock } from './time';

/** The zone the calendar shows times in: the user's profile zone, else the browser's. */
export function displayTimeZone(profileZone: string | null | undefined): string {
  return isValidTimeZone(profileZone) ? profileZone : browserTimeZone();
}

export function useDisplayTimeZone(): string {
  const user = useCurrentUserOrNull();
  return displayTimeZone(user?.timeZone);
}

const INTL_LOCALE: Record<string, string> = { ru: 'ru-RU', en: 'en-GB', uk: 'uk-UA' };
const intlLocale = (language: string) => INTL_LOCALE[language.slice(0, 2)] ?? language;

const capitalize = (text: string) => (text ? text.charAt(0).toLocaleUpperCase() + text.slice(1) : text);

/** Formatting of event times in the display zone (all-day dates in UTC, where they live). */
export interface CalendarFormat {
  timeZone: string;
  intlLocale: string;
  /** 10:00 */
  time: (value: string | Date) => string;
  /** Пн, 19 октября */
  dayLong: (value: string | Date, allDay?: boolean) => string;
  /** 19 окт. */
  dayShort: (value: string | Date, allDay?: boolean) => string;
  /** Пн, 19 окт. · 10:00–10:30 / Пн, 19 окт. – Ср, 21 окт. (весь день) */
  range: (start: string, end: string, allDay: boolean) => string;
  /** пн / понедельник */
  weekday: (code: WeekDayCode, style: 'short' | 'long') => string;
  /** 20 – 24 окт. / 20 окт. — the days of an absence (plain dates `yyyy-MM-dd`, no time zone) */
  dates: (from: string, to: string) => string;
  /** 19.10.2026 (a picker date: its local fields) */
  date: (value: Date) => string;
  /** 19 октября (a picker date: its local fields) */
  dayMonth: (value: Date) => string;
  /** "Октябрь 2026" */
  monthYear: (value: Date) => string;
}

const WEEKDAY_SAMPLE: Record<WeekDayCode, Date> = {
  mo: new Date(Date.UTC(2024, 0, 1)),
  tu: new Date(Date.UTC(2024, 0, 2)),
  we: new Date(Date.UTC(2024, 0, 3)),
  th: new Date(Date.UTC(2024, 0, 4)),
  fr: new Date(Date.UTC(2024, 0, 5)),
  sa: new Date(Date.UTC(2024, 0, 6)),
  su: new Date(Date.UTC(2024, 0, 7)),
};

export function createCalendarFormat(language: string, timeZone: string): CalendarFormat {
  const locale = intlLocale(language);
  const fmt = (options: Intl.DateTimeFormatOptions, allDay = false) =>
    new Intl.DateTimeFormat(locale, { ...options, timeZone: allDay ? 'UTC' : intlTimeZone(timeZone) });
  const time = fmt({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  const dayLong = fmt({ weekday: 'short', day: 'numeric', month: 'long' });
  const dayLongUtc = fmt({ weekday: 'short', day: 'numeric', month: 'long' }, true);
  const dayShort = fmt({ day: 'numeric', month: 'short' });
  const dayShortUtc = fmt({ day: 'numeric', month: 'short' }, true);
  const dayShortWeekday = fmt({ weekday: 'short', day: 'numeric', month: 'short' });
  const dayShortWeekdayUtc = fmt({ weekday: 'short', day: 'numeric', month: 'short' }, true);
  const plainDate = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  const plainDayMonth = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' });
  const monthYear = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' });
  const weekdayShort = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
  const weekdayLong = new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' });

  const sameDay = (a: Date, b: Date) => {
    const wa = toWallClock(a, timeZone);
    const wb = toWallClock(b, timeZone);
    return wa.year === wb.year && wa.month === wb.month && wa.day === wb.day;
  };

  return {
    timeZone,
    intlLocale: locale,
    time: (value) => time.format(new Date(value)),
    dayLong: (value, allDay = false) => capitalize((allDay ? dayLongUtc : dayLong).format(new Date(value))),
    dayShort: (value, allDay = false) => (allDay ? dayShortUtc : dayShort).format(new Date(value)),
    range: (start, end, allDay) => {
      const s = new Date(start);
      const e = new Date(end);
      if (allDay) {
        const last = new Date(e.getTime() - 86_400_000);
        if (last.getTime() <= s.getTime()) return capitalize(dayLongUtc.format(s));
        return `${capitalize(dayShortWeekdayUtc.format(s))} – ${dayShortWeekdayUtc.format(last)}`;
      }
      if (sameDay(s, e) || (e.getTime() - s.getTime() <= 86_400_000 && time.format(e) === '00:00')) {
        return `${capitalize(dayLong.format(s))} · ${time.format(s)}–${time.format(e)}`;
      }
      return `${capitalize(dayShortWeekday.format(s))}, ${time.format(s)} – ${dayShortWeekday.format(e)}, ${time.format(e)}`;
    },
    weekday: (code, style) => (style === 'short' ? weekdayShort : weekdayLong).format(WEEKDAY_SAMPLE[code]),
    dates: (from, to) => {
      const first = new Date(`${from}T00:00:00Z`);
      const last = new Date(`${to}T00:00:00Z`);
      if (Number.isNaN(first.getTime()) || Number.isNaN(last.getTime())) return `${from} – ${to}`;
      return last.getTime() <= first.getTime()
        ? dayShortUtc.format(first)
        : dayShortUtc.formatRange(first, last);
    },
    date: (value) => plainDate.format(value),
    dayMonth: (value) => plainDayMonth.format(value),
    monthYear: (value) => capitalize(monthYear.format(value)),
  };
}

/** Calendar formatting bound to the UI language and the display zone. */
export function useCalendarFormat(): CalendarFormat {
  const { language } = useFormatters();
  const timeZone = useDisplayTimeZone();
  return useMemo(() => createCalendarFormat(language, timeZone), [language, timeZone]);
}

/**
 * i18next formatters for notification templates (`{{start, calendarWhen}}` / `{{start, calendarDate}}`): the bell
 * renders `calendar:notifications.*` with raw ISO parameters, these turn them into "пн, 19 октября, 10:00" in the
 * reader's language and profile zone.
 */
export function registerCalendarFormatters(i18n: I18nInstance): void {
  const formatter = i18n.services.formatter;
  if (!formatter) return;
  const zone = () => displayTimeZone(getAuthState().user?.timeZone);
  formatter.add('calendarWhen', (value: unknown, lng?: string) =>
    formatWhen(value, lng ?? 'ru', zone(), false),
  );
  formatter.add('calendarDate', (value: unknown, lng?: string) =>
    formatWhen(value, lng ?? 'ru', zone(), true),
  );
}

export function formatWhen(value: unknown, language: string, timeZone: string, allDay: boolean): string {
  if (typeof value !== 'string' && !(value instanceof Date)) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const options: Intl.DateTimeFormatOptions = allDay
    ? { weekday: 'short', day: 'numeric', month: 'long', timeZone: 'UTC' }
    : {
        weekday: 'short',
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        timeZone: intlTimeZone(timeZone),
      };
  return new Intl.DateTimeFormat(intlLocale(language), options).format(date);
}
